import dayjs from 'dayjs';
import { ActionItem } from './actions';
import { BadgeStatusSubvariantEnum, BadgeVariantEnum } from '@app/types/badge';
import {
  Document,
  DocumentReviewStatus,
  UploadManager,
  UploadProgressEvent,
  UploadStatus,
} from '@app/types/document';
import { formatCurrency } from './formatCurrency';
import { get } from 'lodash-es';
import { getBadge } from '@app/library/badge/Badge';
import { getDocumentDownloadLink } from '@app/page-fragments/dashboard/documents/components/DocumentDownloadLink';
import { getFormattedMessage } from '@app/components/formatted-message/FormattedMessage';
import { getQuickActions } from '@app/library/quick-actions/QuickActions';
import { getTooltip } from '@app/components/order-summary/Tooltip';
import { TooltipType } from '@app/types/tooltip';

export const getFormattedCurrency = (data: Document, keys?: string[]) => {
  if (!keys) {
    return undefined;
  }
  const amount = get(data, keys);
  return formatCurrency(amount);
};

export const documentDateTransformer = (data: Document, keys?: string[]) => {
  if (!keys) {
    return undefined;
  }
  return dayjs(get(data, keys)).format('MMM DD YYYY');
};

export const documentUTCDateTransformer = (data: Document, keys?: string[]) => {
  if (!keys) {
    return undefined;
  }
  return dayjs.utc(get(data, keys)).format('MMM DD YYYY');
};

export const getDocumentStatus = (data: Document) => {
  const status = data.status_id;

  switch (status) {
    case DocumentReviewStatus.APPROVED:
      return getBadge({
        label: getFormattedMessage({ data: { id: 'documents.status.approved' } }),
        variant: BadgeVariantEnum.STATUS,
        subvariant: BadgeStatusSubvariantEnum.APPROVED,
      });
    case DocumentReviewStatus.DRAFT:
      return getBadge({
        label: [
          getFormattedMessage({ data: { id: 'documents.status.pending' } }),
          getTooltip(TooltipType.DOCUMENT_PENDING_TOOLTIP),
        ],
        variant: BadgeVariantEnum.STATUS,
        subvariant: BadgeStatusSubvariantEnum.PENDING,
      });
    case DocumentReviewStatus.SUBMITTED:
      return getBadge({
        label: getFormattedMessage({ data: { id: 'documents.status.inReview' } }),
        variant: BadgeVariantEnum.STATUS,
        subvariant: BadgeStatusSubvariantEnum.IN_REVIEW,
      });
    case DocumentReviewStatus.NOT_APPROVED:
    case DocumentReviewStatus.ABANDONED:
    case DocumentReviewStatus.MALWARE_DETECTED:
      return getBadge({
        label: [
          getFormattedMessage({ data: { id: 'documents.status.rejected' } }),
          getTooltip(TooltipType.DOCUMENT_NOT_APPROVED_TOOLTIP),
        ],
        variant: BadgeVariantEnum.STATUS,
        subvariant: BadgeStatusSubvariantEnum.REJECTED,
      });
    case DocumentReviewStatus.EXPIRED:
      return getBadge({
        label: getFormattedMessage({ data: { id: 'documents.status.expired' } }),
        variant: BadgeVariantEnum.STATUS,
        subvariant: BadgeStatusSubvariantEnum.EXPIRED,
      });
  }
};

export const getDocumentActions = (data: Document) => {
  const actions: ActionItem[] = [
    {
      type: 'function',
      getElement: ({ onSuccess, ref }) =>
        getDocumentDownloadLink(data, 'view_download', onSuccess, ref),
    },
  ];

  return getQuickActions({ actions });
};

export const handleUploadProgress = (
  uploadManager: UploadManager,
  progressEvent: UploadProgressEvent,
) => {
  if (uploadManager.item?.id && progressEvent.total) {
    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
    uploadManager.actions.updateProgress(
      uploadManager.item.id,
      percentCompleted,
      UploadStatus.UPLOADING,
    );
  }
};
